import { FC } from 'react';
import type { RemediationReviewItem, ReviewDecision } from '@libs/types';
import {
  alpha,
  Box,
  Button,
  Paper,
  type SxProps,
  type Theme,
  Typography,
  useTheme,
} from '@mui/material';
import { observer } from 'mobx-react-lite';

import { RemediationItemStatus } from '@libs/constants';
import { useTrans } from '@libs/i18n';
import { CheckIcon, SparkleIcon, WarningCircleIcon, XIcon } from '@phosphor-icons/react';

/**
 * Styling for a whole-document decision button.
 *
 * A generated document is a choice between two outcomes rather than a primary
 * action and its alternative, so the buttons carry the outcome's colour: green
 * keeps the document, red discards it. The chosen one fills, the other stays an
 * outline, which is what makes the current decision readable without reading
 * the labels — a primary/secondary pair says "selected" but not "selected
 * *what*".
 */
function decisionSx(theme: Theme, active: boolean, main: string, hover: string): SxProps<Theme> {
  return {
    backgroundColor: active ? main : 'transparent',
    color: active ? theme.palette.common.white : main,
    border: `1px solid ${main}`,
    // The pair shares one narrow row; without this the longer label sets a
    // floor and the two of them overflow the rail instead of shrinking.
    minWidth: 0,
    px: 1,
    '&:hover': {
      backgroundColor: active ? hover : alpha(main, 0.08),
      borderColor: hover,
      color: active ? theme.palette.common.white : hover,
    },
    // `sx` outranks the MuiButton root override, so the disabled look has to be
    // restated here or a button stays fully coloured while a decision is saving.
    '&.Mui-disabled': {
      backgroundColor: theme.palette.action.disabledBackground,
      color: theme.palette.action.disabled,
      borderColor: 'transparent',
    },
  };
}

/** Rail section heading, matching the uppercase strip the document panes use. */
const SectionLabel: FC<{ label: string; color?: string }> = observer(({ label, color }) => (
  <Typography
    sx={{
      fontSize: 10.5,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: color ?? 'text.tertiary',
      mb: 0.75,
    }}
  >
    {label}
  </Typography>
));

/**
 * The rail's panel for a generated document.
 *
 * A GENERATE item has no change set, so the rail used to disappear entirely and
 * the reviewer got a page of prose with two buttons at the bottom and no account
 * of where any of it came from. This is that account: what the AI wrote the
 * document against (the clauses it covered, and any it could not), what it left
 * for the user to fill in, and what it wants a second pair of eyes on — with the
 * whole-document decision in the same place the per-change decision lives.
 *
 * Everything here is the AI's own additive metadata (`aiMetadata`); none of it
 * is interpreted or scored on this side.
 */
export const ReviewGenerationPanel: FC<{
  item: RemediationReviewItem;
  busy: boolean;
  onDecide: (decision: ReviewDecision) => void;
}> = observer(({ item, busy, onDecide }) => {
  const theme = useTheme();
  const { t } = useTrans();

  const meta = item.aiMetadata;
  const coverage = meta?.validation?.clauseCoverage;
  const covered = coverage?.covered ?? [];
  const unaddressed = coverage?.unaddressed ?? [];
  const placeholders = meta?.placeholdersToFill ?? [];
  const flags = meta?.flags ?? [];
  const judge = meta?.validation?.judge;
  const judgeNotes = judge?.verdict === 'warn' ? judge.notes : [];

  const nothingToShow =
    covered.length === 0 &&
    unaddressed.length === 0 &&
    placeholders.length === 0 &&
    flags.length === 0 &&
    judgeNotes.length === 0;

  const accepted = item.status === RemediationItemStatus.ACCEPTED;
  const rejected = item.status === RemediationItemStatus.REJECTED;

  return (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius: 4,
        overflow: 'hidden',
        // Same shape as the focused-change panel — header, detail, decision —
        // at whatever height the AI's account of the document needs. The rail
        // scrolls; the panel does not clip itself.
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.75,
          flexShrink: 0,
          backgroundColor: theme.palette.grey[200],
          borderBottom: `1px solid ${theme.palette.grey[300]}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
          <SparkleIcon size={13} color={theme.palette.ocean[700]} />
          <Typography
            sx={{
              fontSize: 11.5,
              fontWeight: 600,
              color: theme.palette.ocean[700],
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            {t('app.remediation.review.generationTitle')}
          </Typography>
        </Box>
        <Typography variant="textSM_semiBold" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
          {item.documentName ?? ''}
        </Typography>
        <Typography variant="textXS_regular" sx={{ color: 'text.secondary' }}>
          {t('app.remediation.review.wholeDocumentHint')}
        </Typography>
      </Box>

      <Box sx={{ px: 2, pt: 2 }}>
        {nothingToShow && (
          <Typography variant="textXS_regular" sx={{ color: 'text.tertiary' }}>
            {t('app.remediation.review.noGenerationDetail')}
          </Typography>
        )}

        {/* A warn verdict is "worth a closer look", never a compliance call —
            it reads as a note, not as a blocker on the decision below. */}
        {judgeNotes.length > 0 && (
          <Box
            sx={{
              backgroundColor: alpha(theme.palette.warning.main, 0.08),
              border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
              borderRadius: 2.5,
              px: 1.5,
              py: 1.25,
              mb: 1.75,
            }}
          >
            <SectionLabel
              label={t('app.remediation.review.qualityNote')}
              color={theme.palette.warning.dark}
            />
            {judgeNotes.map((note, i) => (
              <Typography key={i} sx={{ fontSize: 12, lineHeight: 1.6 }}>
                {note}
              </Typography>
            ))}
          </Box>
        )}

        {placeholders.length > 0 && (
          <Box
            sx={{
              backgroundColor: theme.palette.ocean[100],
              border: `1px solid ${theme.palette.ocean[300]}`,
              borderRadius: 2.5,
              px: 1.5,
              py: 1.25,
              mb: 1.75,
            }}
          >
            <SectionLabel
              label={t('app.remediation.review.placeholders')}
              color={theme.palette.ocean[700]}
            />
            <Typography sx={{ fontSize: 12, lineHeight: 1.6, mb: 0.75 }}>
              {t('app.remediation.review.placeholdersHint')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {placeholders.map((p) => (
                <Box
                  key={p.token}
                  sx={{
                    fontSize: 11,
                    fontFamily: 'monospace',
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.ocean[300]}`,
                    borderRadius: 1.5,
                    px: 0.75,
                    py: 0.25,
                    // `{{data_retention_period}}` has nothing to break on and is
                    // wider than the rail at its narrowest.
                    maxWidth: '100%',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {p.label || p.token}
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {covered.length > 0 && (
          <Box sx={{ mb: 1.75 }}>
            <SectionLabel label={t('app.remediation.review.sourceClauses')} />
            {covered.map((clause, i) => (
              <Box
                key={`${clause.articleLabel}-${i}`}
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 0.75,
                  mb: 0.5,
                  // The section list is the part that runs long, so it drops
                  // under its clause label rather than off the rail.
                  flexWrap: 'wrap',
                }}
              >
                <Typography
                  sx={{ fontSize: 12, fontWeight: 600, color: 'primary.main', flexShrink: 0 }}
                >
                  {clause.articleLabel}
                </Typography>
                {clause.sectionKeys.length > 0 && (
                  <Typography
                    sx={{ fontSize: 11.5, color: 'text.secondary', overflowWrap: 'anywhere' }}
                  >
                    {clause.sectionKeys.join(' · ')}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}

        {/* Named, not just counted: "3 clauses not addressed" tells a reviewer
            they have a problem but not where to look. */}
        {unaddressed.length > 0 && (
          <Box sx={{ mb: 1.75 }}>
            <SectionLabel
              label={t('app.remediation.review.unaddressedClauses', { count: unaddressed.length })}
              color={theme.palette.warning.dark}
            />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {unaddressed.map((clause, i) => (
                <Typography
                  key={`${clause.articleLabel}-${i}`}
                  sx={{
                    fontSize: 11.5,
                    color: 'warning.dark',
                    backgroundColor: alpha(theme.palette.warning.main, 0.1),
                    borderRadius: 1.5,
                    px: 0.75,
                    py: 0.25,
                    maxWidth: '100%',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {clause.articleLabel}
                </Typography>
              ))}
            </Box>
          </Box>
        )}

        {flags.length > 0 && (
          <Box sx={{ mb: 1.75 }}>
            <SectionLabel label={t('app.remediation.review.flagsTitle')} />
            {flags.map((flag) => (
              <Box
                key={flag.flagId}
                sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 0.75 }}
              >
                <WarningCircleIcon
                  size={13}
                  color={theme.palette.text.tertiary}
                  style={{ marginTop: 2, flexShrink: 0 }}
                />
                <Typography sx={{ fontSize: 11.5, color: 'text.secondary', lineHeight: 1.5 }}>
                  {flag.note}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <Box sx={{ px: 2, pt: 2, pb: 2, flexShrink: 0, backgroundColor: 'background.paper' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            fullWidth
            variant="secondary"
            disabled={busy}
            startIcon={<CheckIcon size={15} />}
            sx={decisionSx(theme, accepted, theme.palette.green[700], theme.palette.green[800])}
            onClick={() => onDecide('ACCEPTED')}
          >
            {t('app.remediation.review.accept')}
          </Button>
          <Button
            fullWidth
            variant="secondary"
            disabled={busy}
            startIcon={<XIcon size={14} />}
            sx={decisionSx(theme, rejected, theme.palette.error.main, theme.palette.error.dark)}
            onClick={() => onDecide('REJECTED')}
          >
            {t('app.remediation.review.reject')}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
});
